import { DataManagementConfig } from '@shared/components/data-management';
import { AddUniversityAdminDialog } from '../components/AddUniversityAdminDialog';
import { UniversityAdminTableData, UniversityAdminFormData } from '../types';
import {
  useUniversityAdminsTableData,
  useInviteUniversityAdmin,
  useRemoveUniversityAdmin,
} from '../hooks/useUniversityAdmins';
import { RoleChip } from '@shared/components/RoleChip';
import { userInvitationApi } from '@features/user-invitation/api/userInvitationApi';
import { notificationService } from '@shared/components/notifications';

export const createUniversityAdminsConfig = (
  tenantId: string,
  removeUniversityAdminMutation?: any,
  _confirmFn?: any
): DataManagementConfig<
  UniversityAdminTableData,
  UniversityAdminFormData,
  UniversityAdminFormData
> => ({
  hooks: {
    useQuery: () => useUniversityAdminsTableData(tenantId),
    useCreate: () => useInviteUniversityAdmin(tenantId),
    useDelete: removeUniversityAdminMutation ? () => useRemoveUniversityAdmin(tenantId) : undefined,
  },

  table: {
    title: 'University Administrators',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      {
        key: 'role',
        label: 'Role',
        render: (role) =>
          role && typeof role === 'string' ? <RoleChip role={role} size="small" /> : null,
      },
      {
        key: 'assignedAt',
        label: 'Assigned At',
        render: (date) =>
          date && typeof date === 'string' ? new Date(date).toLocaleDateString() : null,
      },
      {
        key: 'status',
        label: 'Status',
        render: (status) => (status === 'active' ? 'Active' : 'Invited'),
      },
    ],
    emptyMessage: 'No university administrators found for this university',
    errorMessage: 'Failed to load university administrators. Please try refreshing the page.',
  },

  actions: {
    create: {
      label: 'Invite admin user',
      dialog: (props) => <AddUniversityAdminDialog {...props} tenantId={tenantId} />,
    },
    delete: removeUniversityAdminMutation
      ? {
          label: 'Remove',
          confirmTitle: 'Remove University Admin',
          confirmMessage: (admin) =>
            `Remove ${admin.name || admin.email} as university administrator?`,
          transformParams: (admin) => ({
            userId: admin.userId,
            userRoleId: admin.userRoleId,
            userName: admin.name || admin.email,
          }),
        }
      : undefined,
    custom: [
      {
        label: 'Resend Invite',
        onClick: async (admin) => {
          try {
            const response = await userInvitationApi.resendInvitation(admin.email);
            if (response.data?.success) {
              notificationService.success(`Invitation resent to ${admin.email}`);
            } else {
              notificationService.error(
                response.data?.errorMessage || response.message || 'Failed to resend invitation'
              );
            }
          } catch (error) {
            console.error('Failed to resend invitation:', error);
            notificationService.error('Failed to resend invitation');
          }
        },
        color: 'primary',
        variant: 'text',
        condition: (admin) => admin.status === 'invited',
      },
    ],
  },

  features: {
    search: true,
    pagination: true,
  },
});
